import React, { useState, useEffect, useRef, useContext } from "react";
import { useFormik } from "formik";
import "../../assets/css/--component-account-profile.css";
import { changePassword, getUserData, postUpdateProfile, setProfilePicture } from "../../helper/backend-methods";
import nationalityTranslation from "../../data/nationalities";
import appboy from "appboy-web-sdk";
import T, { _T } from "../../locale/translations-main";
import { analyticsTrackEvent } from "../../components/Analytics";
import { Row, Col } from "reactstrap";
import loadImage from "../../assets/images/picture-loading.gif";
import profileImage from "../../assets/images/profile-page-image.png";
import imgEditIcon from "../../assets/images/img-edit-icon.png";
import ConfirmationModal from "../../components/StatusModal";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/userContext";

function ProfileCard({ item, methodUpdate, hasHeading = false }) {
  const [isEditable, setIsEditable] = useState(false);
  const [isEditablePassword, setIsEditablePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState("en");
  const [enableEmail, setEnableEmail] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [values, setValues] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    showPasswordField: false,
    showOldPasswordField: false,
    showNewPasswordField: false,
    showConfirmPasswordField: false,
  });
  const [picture, setPicture] = useState(loadImage);
  const [errors, setErrors] = useState({ occasionImage: "", passwordError: "" });
  const fileInputRef = useRef(null);
  const [passwordError, setPasswordError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState({
    title: <T>Profile Updated</T>,
    message: <T>Your profile have been saved and your profile is now up to date.</T>,
  });
  const history = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const handleCancelSubmit = () => {
    setShowModal(false);
    history("/account/dashboard");
  };

  useEffect(() => {
    if (item) {
      setEnableEmail(item.Email && item.Email.length ? false : true);
      getUserData()
        .then(({ data }) => {
          if (data.Status) {
            setPicture(data.Data.Picture && data.Data.Picture.length ? data.Data.Picture : loadImage);
          } else {
            setPicture(profileImage);
          }
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, [item]);

  

  const formatPhoneForDisplay = (value) => {
    if (!value) return "";
    
    const cleaned = value.replace(/\D/g, "");
    
    const digits = cleaned.slice(-10);
    
    if (digits.length === 0) return "";
    if (digits.length <= 3) return `+1(${digits}`;
    if (digits.length <= 6) return `+1(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `+1(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const normalizePhoneForDB = (value) => {
    const cleaned = value.replace(/\D/g, "");
    
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    } else if (cleaned.length > 11) {
      return `+1${cleaned.slice(-10)}`;
    }
    return `+1${cleaned}`;
  };



  const formik = useFormik({
    initialValues: {
      firstname: item.FirstName ? item.FirstName : "",
      lastname: item.LastName ? item.LastName : "",
      email: item.Email ? item.Email : "",
      phone: item.PhoneNo ? item.PhoneNo : "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (values) => {
      const errors = {};
      const emailRegex = /^\w+([\.-]?\w+)*(\+\w+)?@\w+([\.-]?\w+)*(\.\w{2,10})+$/;
      if (!/^[a-zA-Z\u0600-\u06FF]+(\s[a-zA-Z\u0600-\u06FF]+)*$/.test(values.firstname.trim())) {
        errors.firstname = <T>Please provide a valid first name</T>;
      }
      if (!/^[a-zA-Z\u0600-\u06FF]+$/.test(values.lastname.trim())) {
        errors.lastname = <T>Please provide a valid last name</T>;
      }
      if (!emailRegex.test(values.email)) {
        errors.email = <T>Please provide a valid email address</T>;
      }
      if (!values.phone || !/^(?:\+1\s?)?(?:\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}$/.test(values.phone)) {
        errors.phone = <T>Please provide a valid phone number</T>;
      }
      return errors;
    },
    onSubmit: (values) => {
      setLoading(true);
      setEmailError("");
      setPhoneError("");
      postUpdateProfile({
        FirstName: values.firstname.trim().replace(/[^a-zA-Z\u0600-\u06FF ]/g, ""),
        LastName: values.lastname.trim().replace(/[^a-zA-Z\u0600-\u06FF ]/g, ""),
        Email: values.email,
        Phone: normalizePhoneForDB(values.phone),
      })
        .then(({ data }) => {
          if (data.Status) {
            methodUpdate({
              ...item,
              FirstName: values.firstname.trim(),
              LastName: values.lastname.trim(),
              Gender: values.gender,
              Nationality: values.nationality,
              Email: values.email,
              PhoneNo: normalizePhoneForDB(values.phone),
            });
            setIsEditable(false);
            setShowModal(true);
            setLocalUserInfo("FullName", values.firstname.trim() + " " + values.lastname.trim());
            setLocalUserInfo("Phone", normalizePhoneForDB(values.phone));
            analyticsTrackEvent("Profile Updated", { screen_name: "Account Profile" });
            //document.getElementById("profileName").innerHTML = values.firstname + " " + values.lastname
            //document.getElementById("profileEmail").innerHTML = values.email;
            if (values.firstname) {
              appboy.getUser().setFirstName(values.firstname.replace(/[^a-zA-Z\u0600-\u06FF ]/g, ""));
            }
            if (values.lastname) {
              appboy.getUser().setLastName(values.lastname.replace(/[^a-zA-Z\u0600-\u06FF ]/g, ""));
            }
            if (values.email) {
              appboy.getUser().setEmail(values.email);
            }
            if (values.phone) {
              appboy.getUser().setPhoneNumber(values.phone);
            }
          } else {
            if (data.StatusCode === 1007) {
              setEmailError(_T("The email you have provided is already associated with another account."));
            }
          }
          setLoading(false);
        })
        .catch((e) => {
          console.log(e);
          setIsEditable(false);
          setLoading(false);
        });
    },
  });

  const updateProfilePicture = (e) => {
    var allowedExtension = ["jpeg", "jpg", "png"];
    const errors = { occasionImage: "" };
    if (e.target.files && e.target.files[0]) {
      if (!allowedExtension.includes(e.target.value.split(".").pop().toLowerCase())) {
        errors.occasionImage = <T>Allowed image types are jpeg, jpg, png only.</T>;
      } else {
        setPicture(loadImage);
        const formData = new FormData();
        formData.append("picture", e.target.files[0]);
        setProfilePicture(formData)
          .then(({ data }) => {
            console.log(data);
            var reader = new FileReader();
            reader.onload = function (e) {
              setPicture(e.target.result);
              setLocalUserInfo("ProfilePicture", e.target.result);
              analyticsTrackEvent("Profile Picture Updated", { screen_name: "Account Profile" });
            };
            reader.readAsDataURL(e.target.files[0]);
          })
          .catch((e) => {
            console.log(e);
          });
      }
    }
    setErrors(errors);
  };

  const updateChangePassword = () => {
    setPasswordError("");

    if (!/^(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?#&]{8,}$/.test(values.newPassword.trim())) {
      setPasswordError("Password must contain at least 1 uppercase letter, 1 number, and be at least 8 characters long");
      return;
    }

    if (values.oldPassword === values.newPassword) {
      setPasswordError("Old Password and New Password should not be the same.");
      return;
    }

    if (values.newPassword !== values.confirmPassword) {
      setPasswordError("New Password and Confirm Password do not match.");
      return;
    }

    changePassword({ OldPassword: values.oldPassword, NewPassword: values.newPassword })
      .then(({ data }) => {
        if (data.Status) {
          setIsEditable(false);
          setIsEditablePassword(false);
          setValues({ OldPassword: "", NewPassword: "", ConfirmPassword: "", showPasswordField: false });
          setPasswordError("");
          analyticsTrackEvent("Password Changed", { screen_name: "Account Profile" });
        } else {
          setPasswordError(data.Message);
          if (data.StatusCode === 1007) {
            setEmailError(_T("The email you have provided is already associated with another account."));
          }
        }
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setIsEditable(false);
        setLoading(false);
      });
  };

  const setLocalUserInfo = (key, value) => {
    let data = JSON.parse(localStorage.getItem("userinfo"));
    data[key] = value;
    localStorage.setItem("userinfo", JSON.stringify(data));
    setUser(data);
  };

  return (
    <>
      <div className="mt-5 ms-4">
        <div className="user-profile-inner">
          <section id="profile-page" className="profile-section">
            <div className="profile-body-header mb-4">
              <h1 className="fs-30 text-blakish fw-600 mt-0 mb-0">Profile Information</h1>
              <span className="separator my-3" />
            </div>

            <div className="profile-header">
              <div className="d-flex align-items-center gap-3 mt-3 mb-3">
                <div htmlFor="profilePicture" data-text="Change">
                  <div className="position-relative">
                    <img className="profile-img" src={picture} alt="Profile Image" />
                    <input
                      className="d-none"
                      type={"file"}
                      id="profilePicture"
                      ref={fileInputRef}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = profileImage;
                      }}
                      onChange={updateProfilePicture}
                    />
                    <img src={imgEditIcon} alt="Edit Icon" className="img-edit-icon" onClick={() => fileInputRef.current.click()} />
                  </div>
                </div>
                <div>
                  <h4 className="fs-24 text-black">
                    {formik.values.firstname.replace(/[^a-zA-Z\u0600-\u06FF ]/g, "")}{" "}
                    {formik.values.lastname.replace(/[^a-zA-Z\u0600-\u06FF ]/g, "")}
                  </h4>
                  <p className="text-gray-700 fs-14 m-0">{formik.values.email}</p>
                </div>
              </div>
              <div className="edit-btn">
                {isEditable ? (
                  <div className="save-cancel justify-end d-flex pb-0">
                    {loading ? (
                      <button className="save-btn fs-16 me-2" type="button">
                        <T>Please Wait</T>
                      </button>
                    ) : (
                      <button className="save-btn fs-16 me-2" type="button" onClick={formik.handleSubmit}>
                        <T>SAVE CHANGES</T>
                      </button>
                    )}
                    <button
                      type="button"
                      className="cancel-btn fs-16 mr-2"
                      onClick={() => {
                        setIsEditable(false);
                        history(0);
                      }}
                    >
                      <T>Cancel</T>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="fs-16 align-items-center d-inline-flex"
                    onClick={() => {
                      setIsEditable(true);
                    }}
                  >
                    <i className="ri-edit-2-line icon-position me-2 mb-1 fs-20"></i>EDIT PROFILE
                  </button>
                )}
              </div>
            </div>
            <span className="separator" />
            {isEditable ? (
              <form className="mt-4">
                <div className="inputfields-container mb-4">
                  <div className="fileds-column">
                    <div className="form-content mb-3 pr-2 pl-2">
                      <label className="fw-500 text-blakish fs-16 mt-0 mb-1 d-inline-block">
                        <T>First Name</T>
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        className=""
                        id="firstname"
                        type="text"
                        name="firstname"
                        maxLength={30}
                        onChange={formik.handleChange}
                        value={formik.values.firstname.replace(/[^a-zA-Z\u0600-\u06FF ]/g, "")}
                        placeholder={_T("First Name")}
                      />
                      {formik.errors.firstname ? <span className="text-primary fs-12 fw-400">{formik.errors.firstname}</span> : null}
                    </div>
                    <div className="form-content mb-3 pr-2 pl-2">
                      <label className="fw-500 text-blakish fs-16 mt-0 mb-1 d-inline-block">
                        <T>Last Name</T>
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        className=""
                        id="lastname"
                        type="text"
                        name="lastname"
                        maxLength={30}
                        onChange={formik.handleChange}
                        value={formik.values.lastname.replace(/[^a-zA-Z\u0600-\u06FF ]/g, "")}
                        placeholder={_T("Last Name")}
                      />
                      {formik.errors.lastname ? <span className="text-primary fs-12 fw-400">{formik.errors.lastname}</span> : null}
                    </div>
                  </div>

                  <div className="fileds-column">
                    <div>
                      <div className="form-content mb-3 pr-2 pl-2 position-relative">
                        <i className="ri-mail-line input-icon text-blakish"></i>
                        <label className="fw-500 text-blakish fs-16 mt-0 mb-1 d-inline-block">
                          <T>Email</T>
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          className="with-icon main-profile-email"
                          id="email"
                          type="email"
                          name="email"
                          onChange={formik.handleChange}
                          value={formik.values.email.slice(0, 50)}
                          placeholder="example@Main Country Grill and Store.com"
                          readOnly={!enableEmail}
                        />
                      </div>
                      {formik.errors.email ? (
                        <span className="text-danger fs-12 fw-400">{formik.errors.email}</span>
                      ) : emailError ? (
                        <span className="text-primary fs-12 fw-400">{emailError}</span>
                      ) : null}
                    </div>

                    <div>
                      <div className="form-content mb-3 pr-2 pl-2 position-relative">
                        <i className="ri-phone-line input-icon text-blakish"></i>
                        <label className="fw-500 text-blakish fs-16 mt-0 mb-1 d-inline-block">
                          <T>Contact Number</T>
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          className="with-icon"
                          id="phone"
                          type="tel"
                          name="phone"
                          onChange={formik.handleChange}
                          value={formik.values.phone}
                          maxLength={16}
                          placeholder="+1(XXX) XXX-XXXX"
                          readOnly={false}
                        />
                      </div>
                      {formik.errors.phone ? (
                        <span className="text-danger fs-12 fw-400">{formik.errors.phone}</span>
                      ) : phoneError ? (
                        <span className="text-primary fs-12 fw-400">{phoneError}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <>
                <Row className="mt-5">
                  <Col md={4}>
                    <div className="form-content mb-4">
                      <label className="fw-500 text-blakish fs-16 mt-0 mb-1 d-inline-block">
                        <T>First Name</T>
                        <span className="text-danger">*</span>
                      </label>
                      <p className="fw-400 text-gray fs-18 m-0 word-wrap">{item.FirstName ? item.FirstName : <T>N/A</T>}</p>
                    </div>
                  </Col>
                  <Col md={8}>
                    <div className="form-content mb-4">
                      <label className="fw-500 text-blakish fs-16 mt-0 mb-1 d-inline-block">
                        <T>Last Name</T>
                        <span className="text-danger">*</span>
                      </label>
                      <p className="fw-400 text-gray fs-18 m-0 word-wrap">{item.LastName ? item.LastName : <T>N/A</T>}</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="form-content mb-3">
                      <label className="fw-500 text-blakish fs-16 mt-0 mb-1 d-inline-block">
                        <T>Email</T>
                        <span className="text-danger">*</span>
                      </label>
                      <p className="fw-400 text-gray fs-18 m-0 word-wrap">{item.Email ? item.Email : <T>N/A</T>}</p>
                    </div>
                  </Col>
                  <Col md={8}>
                    <div className="form-content mb-3">
                      <label className="fw-500 text-blakish fs-16 mt-0 mb-1 d-inline-block">
                        <T>Contact Number</T>
                        <span className="text-danger">*</span>
                      </label>
                      <p className="fw-400 text-gray fs-18 m-0 word-wrap">{item.PhoneNo ? formatPhoneForDisplay(item.PhoneNo) : <T>N/A</T>}</p>
                    </div>
                  </Col>
                  {/* <div className="form-content mb-3">
                    <label className="fw-500 text-gray-500 fs-14 mt-0 mb-1 d-inline-block">{translations.ProfileTab[language].nationalityLabel}</label>
                    <p className="fw-400 text-gray-400 m-0 word-wrap">{item.Nationality && item.Nationality !== "0" ? nationalityTranslation(item.Nationality)[language] : translations.ProfileTab[language].notAvailable}</p>
                </div> */}
                </Row>
              </>
            )}
          </section>
          <section id="password-sec" className="password-section mt-3">
            <div className="profile-body-header mb-4">
              <h1 className="fs-30 text-blakish fw-600 mt-0 mb-0">Password & Security</h1>
              <span className="separator my-4" />
            </div>
            {isEditablePassword ? (
              <div className="w-50 password-chnage">
                <div className="form-group has-icon has-pass-icon mt-2">
                  <label htmlFor="oldPassword">
                    Password<span className="txt-danger">*</span>
                  </label>
                  <input
                    id="oldPassword"
                    type={values.showOldPasswordField ? "text" : "password"}
                    name="oldPassword"
                    onChange={(e) => {
                      setValues((prev) => ({ ...prev, oldPassword: e.target.value.replaceAll(/\s/g, "").slice(0, 30) }));
                    }}
                    value={values.oldPassword}
                    placeholder="Password"
                  />
                  <button
                    onClick={() => {
                      setValues((prev) => ({ ...prev, showOldPasswordField: !prev.showOldPasswordField }));
                    }}
                    type="button"
                  >
                    {values.showOldPasswordField ? <i className="ri-eye-off-line"></i> : <i className="ri-eye-line"></i>}
                  </button>
                </div>
                <div className="form-group has-icon has-pass-icon mt-2">
                  <label htmlFor="oldPassword">
                    New Password<span className="txt-danger">*</span>
                  </label>
                  <input
                    id="oldPassword"
                    type={values.showNewPasswordField ? "text" : "password"}
                    name="newPassword"
                    onChange={(e) => {
                      setValues((prev) => ({ ...prev, newPassword: e.target.value.replaceAll(/\s/g, "").slice(0, 30) }));
                    }}
                    value={values.newPassword}
                    placeholder="New Password"
                  />
                  <button
                    onClick={() => {
                      setValues((prev) => ({ ...prev, showNewPasswordField: !prev.showNewPasswordField }));
                    }}
                    type="button"
                  >
                    {values.showNewPasswordField ? <i className="ri-eye-off-line"></i> : <i className="ri-eye-line"></i>}
                  </button>
                  {errors.newPassword ? <span className="txt-danger fs-12 fw-400">{errors.newPassword}</span> : null}
                </div>
                <div className="form-group has-icon has-pass-icon mt-2">
                  <label htmlFor="oldPassword">
                    Confirm Password<span className="txt-danger">*</span>
                  </label>
                  <input
                    id="oldPassword"
                    type={values.showConfirmPasswordField ? "text" : "password"}
                    name="confirmPassword"
                    onChange={(e) => {
                      setValues((prev) => ({ ...prev, confirmPassword: e.target.value.replaceAll(/\s/g, "").slice(0, 30) }));
                    }}
                    value={values.confirmPassword}
                    placeholder="Confirm Password"
                  />
                  <button
                    onClick={() => {
                      setValues((prev) => ({ ...prev, showConfirmPasswordField: !prev.showConfirmPasswordField }));
                    }}
                    type="button"
                  >
                    {values.showConfirmPasswordField ? <i className="ri-eye-off-line"></i> : <i className="ri-eye-line"></i>}
                  </button>
                </div>
                <div className="mt-2">{passwordError ? <span className="txt-danger fs-12 fw-400">{passwordError}</span> : null}</div>
                <div className="save-cancel justify-content-end d-flex pb-0 mt-4 text-right">
                  <button
                    type="button"
                    className="cancel-btn fs-16 me-2"
                    onClick={() => {
                      setIsEditablePassword(false);
                    }}
                  >
                    <T>Cancel</T>
                  </button>
                  {loading ? (
                    <button className="save-btn fs-16 me-2" type="button">
                      <T>Please Wait</T>
                    </button>
                  ) : (
                    <button className="save-btn fs-16 me-2" type="button" onClick={updateChangePassword}>
                      <T>SAVE PASSWORD</T>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="form-group has-icon mt-5">
                  <label htmlFor="passwordField">
                    Password<span className="txt-danger">*</span>
                  </label>
                  <input id="passwordField" placeholder="**************************" readOnly={true} value={""} />
                  {/* <button type="button">
                    {values.showPasswordField ? <i className="ri-eye-off-line"></i> : <i className="ri-eye-off-line"></i>}
                  </button> */}
                </div>
                <span className="view-all-link fs-14 fw-600 mt-4 d-block cursor-pointer" onClick={() => setIsEditablePassword(true)}>
                  CHANGE PASSWORD
                </span>
              </>
            )}
          </section>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showModal}
        toggle={() => setShowModal(false)}
        type="confirmation"
        title={confirmationMessage.title}
        message={confirmationMessage.message}
        ButtonText="Back to Dashboard"
        onCancel={handleCancelSubmit}
      />
    </>
  );
}
export default ProfileCard;
